import type { ReactElement } from "react";
import { ArrowRight } from "lucide-react";
import { ProcessChain } from "../../icons/index.js";
import type { WidgetBodyProps } from "../types.js";
import { useWidgetActions } from "../widget-actions.js";
import type { MyVsmsView } from "./types.js";

export function MyVsmsWidget({
  data,
}: WidgetBodyProps<MyVsmsView>): ReactElement {
  const actions = useWidgetActions();
  return (
    <div className="space-y-3">
      {data.vsms.length === 0 ? (
        <p className="text-sm italic text-pb-text-muted">
          No active value streams yet.
        </p>
      ) : (
        <ul className="divide-y divide-pb-border/60">
          {data.vsms.map((vsm) => (
            <li key={vsm.id}>
              {/* eslint-disable-next-line ds/no-hand-built -- kept: a clickable row with its own inset and a pulled-out hover; ListItem has no inline padding and its own row layout, which AP-07's selection fill does not cover. */}
              <button
                type="button"
                disabled={!actions?.openVsm}
                onClick={() => actions?.openVsm?.(vsm.id, vsm.name)}
                className="flex w-full items-center gap-3 rounded-md px-1 py-2.5 text-start hover:pb-overlay-paint disabled:cursor-default"
              >
                <ProcessChain
                  className="h-4 w-4 shrink-0 text-pb-teal-text"
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-pb-text">
                    {vsm.name}
                  </span>
                  <span className="block truncate text-xs text-pb-text-muted">
                    {[vsm.plant, vsm.productFamily, vsm.role]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="text-dense-2 uppercase text-pb-text-muted">
                  {vsm.lifecycle === "DRAFT" ? "Draft" : "Active"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {actions?.openVsmHome ? (
        // eslint-disable-next-line ds/no-hand-built -- kept: a 14px secondary-ink text action with a chevron; Button link is underlined at inline size, and no approved look row lists the change.
        <button
          type="button"
          onClick={actions.openVsmHome}
          className="inline-flex items-center gap-1 text-sm font-medium text-pb-text-secondary transition-colors hover:text-pb-text focus-ring"
        >
          Open VSM home <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
